import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'

import { fontSizeXSmall, fontWeightBold, grayLight30 } from '../../constants/principles'
import { BrowserUtils, QuestionUtils } from '../../utils/siteUtils'
import { BodyDivFlex } from '../generic/Basics'
import ICOHeader from '../generic/ICOHeader'
import NavFooter from '../generic/navFooter'
import PageHeader from '../generic/PageHeader'
import ConditionModal from './ConditionModal'
import { ContactPageNumber, ExteriorPageNumber, SummaryPageNumber } from '../../constants'
import { useScroller } from '../../hooks'
import ConditionComponentWrapper from './ConditionComponentWrapper'

export default ({
  experienceState,
  offer,
  children,
  urlState,
  title,
  subheader,
  color,
  transmission,
  engine,
  drivetrain,
  mileage,
  year,
  make,
  model,
  trim,
  images,
  noDamage,
  nextClick,
  onNextClicked,
  backClick,
  svgItems,
  svgIcons,
  itemQuestions,
  iconQuestions,
  addImpressions,
  updateGlobalState,
  globalState,
  conditionQuestions = [],
  anyAnswered,
  variation,
  photoSection,
  pageName,
  getHasVehicleModifications,
  saveQuestionResponse,
  amQ,
  movedItems,
  autoCheckData,
  vinResults,
  primaryNavigation,
  setShowModal,
  tab,
  isRequirePhotos,
  anyPhotosUploadedAndTrue,
  shouldUseImageCaptureWebComponent,
  allWebComponentPhotosUploaded,
  photoUploadedUrls,
  ...otherProps
}) => {
  const { displayImages, isIneligibleVehiclesEnabled, vehicleIntoAuction, isAssigned, shouldShowOfferCreationComponent, isCobrand, allowIneligibleVehicle } = experienceState

  // component offer creation flag is on && ineligibleVehicles flag is on && components should show && isAssign offer flow && vehicle is ineligible
  const allowIneligibleVehicleForCobranding = allowIneligibleVehicle && isCobrand
  const allowIneligibleVehicles = isAssigned || allowIneligibleVehicleForCobranding
  const allowIneligibleVehiclesForWebComponent = isIneligibleVehiclesEnabled && shouldShowOfferCreationComponent && allowIneligibleVehicles && !vehicleIntoAuction
  const isDesktop = BrowserUtils.isDesktop()
  const analytics = []
  const [conditionPageIsLoaded, setConditionPageIsLoaded] = useState(false)
  const anySvgTrue = svgItems
    ? svgItems.reduce((acc, cur) => {
        const svgFound = conditionQuestions.find(question => question.subsectionCode && cur.toLowerCase().includes(question.subsectionCode))
        const svgSubSectionCode = (svgFound || {}).subsectionCode || '' // this lets us get around multiple seats issues
        const svgAnyAnswered = anyAnswered(QuestionUtils.getAssociatedQuestions(conditionQuestions, '', svgSubSectionCode))

        analytics.push({ key: svgSubSectionCode, value: svgAnyAnswered })

        return acc || svgAnyAnswered
      }, false)
    : false
  const anyIconTrue = svgIcons
    ? svgIcons.reduce((acc, cur) => {
        const iconFound = conditionQuestions.find(question => question.subsectionCode && question.item.toLowerCase().startsWith(svgIcons[0].toLowerCase()))
        const iconSubSectionCode = (iconFound || {}).subsectionCode || '' // this lets us get around multiple seats issues
        const iconAnyAnswered = anyAnswered(QuestionUtils.getAssociatedQuestions(conditionQuestions, cur, iconSubSectionCode))

        analytics.push({ key: cur, value: iconAnyAnswered })

        return acc || iconAnyAnswered
      }, false)
    : false
  const anyMovedQuestion = movedItems
    ? movedItems.reduce((acc, cur) => {
        const questionFound = conditionQuestions.find(question => question.item.toLowerCase().startsWith(movedItems[0].toLowerCase()))
        const questionSubSectionCode = (questionFound || {}).subsectionCode || ''
        const questionAnyAnswered = anyAnswered(QuestionUtils.getAssociatedQuestions(conditionQuestions, cur, questionSubSectionCode))

        analytics.push({ key: cur, value: questionAnyAnswered })

        return acc || questionAnyAnswered
      }, false)
    : false
  const anyPhotoTrue =
    photoSection &&
    Object.values(photoSection).reduce((acc, curr) => {
      return acc || !!curr.imageData
    }, false)

  const shouldShowConditionWebComponent = () => {
    if (shouldShowOfferCreationComponent && pageName === 'exterior') {
      return true
    }
    updateGlobalState({ showNoDamageModal: true }) //control isPerfect modal popup when web components are turned off
    return false
  }
  const anyTrue = anyIconTrue || anySvgTrue || anyPhotoTrue || anyMovedQuestion
  const isSQR = saveQuestionResponse ? 1 : 0
  const getHasVM = getHasVehicleModifications()
  const shouldDisableNext = isRequirePhotos && (shouldUseImageCaptureWebComponent ? !allWebComponentPhotosUploaded : !anyPhotosUploadedAndTrue)
  const [setHasScrolled, scroll] = useScroller()

  useEffect(() => {
    if (pageName === 'aftermarket' && anyTrue && !getHasVM) {
      saveQuestionResponse && saveQuestionResponse(amQ, 0)
    } else if (pageName === 'aftermarket' && !anyTrue && getHasVM) {
      saveQuestionResponse && saveQuestionResponse(amQ, 1)
    }
    setHasScrolled(true)
    scroll('body', 'top')
  }, [pageName, anyTrue, isSQR, getHasVM, amQ])

  useEffect(() => {
    setConditionPageIsLoaded(true)
  }, [])

  addImpressions && addImpressions(analytics)

  function handleClick() {
    setTimeout(function () {
      if (BrowserUtils.isIframe()) {
        BrowserUtils.emitComponentSize(document.getElementById('conditionPage')?.clientHeight ?? 0, pageName === 'imageCapture' ? 'imagecapture' : 'condition')
      }
    }, 500)
  }

  return (
    <>
      <div id={'conditionPage'} onClick={handleClick}>
        <BodyDivFlex isDesktop={isDesktop}>
          <ICOHeader {...{ displayImages, urlState, year, make, model, images, updateGlobalState, variation, trim, autoCheckData, vinResults, experienceState, globalState, mileage }} />
          {pageName !== 'imageCapture' && <PageHeader text={title} subheader={subheader || 'You know your vehicle best. Tell us where it has signs of wear or damage.'} />}
          {shouldShowConditionWebComponent() ? (
            <ContentDiv>
              <ConditionComponentWrapper
                conditionPageIsLoaded={conditionPageIsLoaded}
                updateGlobalState={updateGlobalState}
                yearId={year.yearId}
                makeId={make.makeId}
                modelId={model.modelId}
                trimId={trim.trimId}
                colorId={color.colorId}
                transmissionId={transmission.transmissionId}
                engineId={engine.engineId}
                drivetrainId={drivetrain.drivetrainId}
                vin={vinResults ? vinResults.vin : null}
                mileage={mileage}
                allowIneligibleVehicleSubmission={allowIneligibleVehiclesForWebComponent}
                {...otherProps}
              />
            </ContentDiv>
          ) : (
            <ContentDiv {...{ isDesktop }}>{children}</ContentDiv>
          )}
          {!anyTrue && noDamage && <StyledH2 {...{ isDesktop }}>{noDamage}</StyledH2>}
        </BodyDivFlex>
      </div>
      <NavFooter
        {...{
          experienceState,
          offer,
          backClick,
          nextClick: (...args) => {
            if (urlState.p === ExteriorPageNumber) {
              args = !urlState.edit || !args[0] ? [ContactPageNumber] : [SummaryPageNumber]
            }
            if (typeof onNextClicked === 'function' && !urlState.edit) onNextClicked()
            if (shouldDisableNext) return
            nextClick(...args)
          },
          globalState,
          step: urlState.p,
          urlState,
          setShowModal,
          updateGlobalState,
          addImpressions,
          tab,
          nextDisabled: shouldShowOfferCreationComponent && shouldDisableNext,
          ...otherProps
        }}
      />
      <ConditionModal {...{ ...otherProps, pageName, saveQuestionResponse, updateGlobalState, globalState, conditionQuestions, anyAnswered, isDesktop, setShowModal }} />
    </>
  )
}

const StyledH2 = styled.h2`
  font-size: ${fontSizeXSmall};
  font-weight: ${fontWeightBold};
  width: ${p => (p.isDesktop ? 80 : 100)}%;
  text-align: center;
  border-top: 1px solid ${grayLight30};
  margin-top: 0px;
  padding-top: 40px;
  margin-bottom: 32px;
`
const ContentDiv = styled.div`
  width: ${p => (p.isDesktop ? (p.primaryNavigation ? 80 : 100) : 100)}%;
  text-align: center;
  padding: 32px 0;
  min-height: 250px;
`
